// Centralized error reporting.
// All error logging in the app should go through logError() so that we have
// one place to swap in Sentry or similar later without touching call sites.
//
// Usage:
//   try { ... } catch (e) { logError(e, 'context: what was being done'); }
//   try { ... } catch (e) { logError(e, 'saveLesson', { lessonId }); }

const isProd = typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';

export function logError(error, context = '', extra = null) {
  const payload = {
    message: error?.message || String(error),
    stack: error?.stack,
    context,
    extra,
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    timestamp: new Date().toISOString(),
  };

  // Always log to console — visible in DevTools
  // eslint-disable-next-line no-console
  console.error(`[${context || 'error'}]`, error, extra ?? '');

  // In production, also queue for remote reporting (Sentry hook goes here later)
  if (isProd) {
    try {
      // Placeholder — when Sentry is wired up:
      // window.Sentry?.captureException(error, { extra: { context, ...extra } });
      window.__lastErrors = window.__lastErrors || [];
      window.__lastErrors.push(payload);
      if (window.__lastErrors.length > 50) window.__lastErrors.shift();
    } catch (_) { /* never throw from the reporter itself */ }
  }
}

// Convenience wrapper for fire-and-forget promises where we only want logging,
// no user-facing feedback. Replaces patterns like .catch(() => {}).
export function logAndSwallow(context) {
  return (error) => logError(error, context);
}

// Install global handlers — runs once at app startup.
// Catches any error/rejection that escapes try/catch blocks throughout the app.
let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), 'window.onerror', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason || new Error('Unhandled promise rejection'), 'unhandledrejection');
  });
}
